import { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import type { Scene } from '@/types';
import { elegirOpcion, entrarAEscena, opcionesAnotadas } from '@/engine/narrative';
import { SceneBackgroundLayer } from './SceneBackground';
import { TypewriterText } from './TypewriterText';
import { ChoiceList } from './ChoiceList';
import { CombatScreen } from '@/components/game/Combat';
import { Button } from '@/components/ui';

interface StorySceneProps {
  scene: Scene;
  onTransicion?: (nuevaScene: Scene) => void;
}

/**
 * Renderiza la escena con todos sus párrafos visibles a la vez. El typewriter
 * los revela en secuencia; cuando termina el último, aparecen las opciones
 * (o se arma el combate, o se cierra el final).
 *
 * Para mantener todo dentro del viewport sin scroll de página, los párrafos
 * viven en un contenedor con bounded-scroll interno; con auto-scroll al
 * final a medida que se revelan.
 */
export function StoryScene({ scene, onTransicion }: StorySceneProps) {
  const [indiceActivo, setIndiceActivo] = useState(0);
  const [todoRevelado, setTodoRevelado] = useState(false);
  const [combateActivo, setCombateActivo] = useState(false);

  const hayParrafos = scene.texto.length;

  useEffect(() => {
    setIndiceActivo(0);
    setTodoRevelado(hayParrafos === 0);
    setCombateActivo(false);
  }, [scene.id, hayParrafos]);

  const avanzarParrafo = () => {
    if (indiceActivo + 1 < hayParrafos) {
      setIndiceActivo((i) => i + 1);
    } else {
      setTodoRevelado(true);
    }
  };

  const opciones = useMemo(() => opcionesAnotadas(scene), [scene]);

  const elegir = (choiceId: string) => {
    try {
      const r = elegirOpcion(scene, choiceId);
      onTransicion?.({ ...scene, id: r.destino });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('No se pudo elegir la opción:', err);
    }
  };

  const terminarCombate = (resultado: 'victoria' | 'derrota' | 'huida') => {
    if (!scene.combate) return;
    const destino =
      resultado === 'victoria'
        ? scene.combate.onVictoria
        : resultado === 'derrota'
          ? scene.combate.onDerrota
          : scene.combate.onHuida;
    const nueva = entrarAEscena(destino);
    onTransicion?.(nueva);
  };

  const esFinal = scene.final !== undefined;
  const esCombate = scene.combate !== undefined;

  if (esCombate && combateActivo) {
    return <CombatScreen enemyId={scene.combate!.enemyId} onSalir={terminarCombate} />;
  }

  return (
    <div className="relative h-full w-full overflow-hidden">
      <SceneBackgroundLayer background={scene.background} sceneKey={scene.id} />

      <div className="relative z-10 h-full flex flex-col px-[clamp(1rem,4vmin,3rem)] py-[clamp(0.75rem,2.5vmin,1.75rem)] min-h-0">
        {scene.titulo && (
          <header className="shrink-0 mb-[clamp(0.5rem,2vmin,1rem)]">
            <h1 className="fluid-xl text-display text-ember-300/90 tracking-[0.2em] uppercase text-center">
              {scene.titulo}
            </h1>
          </header>
        )}

        {/* Párrafos: contenidos en un bloque centrado con scroll-y interno
            bounded si el texto excede el espacio. */}
        <div className="flex-1 min-h-0 flex items-center justify-center w-full">
          <motion.div
            key={scene.id}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: 'easeOut' }}
            className="w-full max-w-3xl mx-auto max-h-full scroll-y"
          >
            <div className="text-narrative fluid-prose text-ash-100 space-y-4 text-center px-2">
              {scene.texto.map((parrafo, i) => {
                if (i > indiceActivo) return null;
                const esActual = i === indiceActivo;
                return (
                  <TypewriterText
                    key={`${scene.id}:${i}`}
                    texto={parrafo}
                    iniciarCompleto={!esActual}
                    onComplete={esActual ? avanzarParrafo : undefined}
                  />
                );
              })}
            </div>
          </motion.div>
        </div>

        <div className="shrink-0 mt-[clamp(0.5rem,2vmin,1.25rem)] flex flex-col items-center gap-[clamp(0.5rem,2vmin,1rem)]">
          {todoRevelado && esFinal && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4, duration: 1 }}
              className="text-center"
            >
              <p className="fluid-xs uppercase tracking-[0.4em] text-ember-500 font-display">
                Final alcanzado
              </p>
              <p className="fluid-lg text-display text-ash-100 mt-1">{scene.final}</p>
            </motion.div>
          )}

          {todoRevelado && esCombate && !combateActivo && (
            <Button variant="danger" size="md" onClick={() => setCombateActivo(true)}>
              Empezar combate
            </Button>
          )}

          {todoRevelado && !esFinal && !esCombate && (
            <div className="w-full max-w-3xl">
              <ChoiceList opciones={opciones} onElegir={elegir} visible />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
